'use client';


/** Imports */
import {Link} from '@/i18n/routing';
import {useMemo, useState} from 'react';
import {useTranslations} from 'next-intl';
import styles from '../helpCenter.module.css';
import {LuArrowRight, LuSearch, LuX} from 'react-icons/lu';
import {type HelpArticle} from '@/content/helpCenter.generated';


/** Types */
type SearchArticle = Readonly<Pick<HelpArticle, 'slug' | 'title' | 'summary'> & {categoryTitle: string}>;
type Props = Readonly<{
    articles: Array<SearchArticle>;
}>;


/** Component */
export default function HelpCenterSearch({articles}: Props) {

    // Hooks
    const t = useTranslations('HelpCenter');

    // State
    const [query, setQuery] = useState('');

    // Memos
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const results = useMemo(() => {
        if (!normalizedQuery) return [];
        const searchTerms = normalizedQuery.split(/\s+/);
        return articles.filter((article) => {
            const searchableText = `${article.title} ${article.summary} ${article.categoryTitle}`
                .toLocaleLowerCase();
            return searchTerms.every((term) => searchableText.includes(term));
        }).slice(0, 40);
    }, [articles, normalizedQuery]);


    // Render
    return (
        <div className={styles.searchArea}>
            <div className={styles.searchBox}>
                <LuSearch aria-hidden="true"/>
                <input
                    type="search"
                    value={query}
                    aria-label={t('search.label')}
                    placeholder={t('search.placeholder')}
                    onChange={(event) => setQuery(event.target.value)}
                />
                {(query) && (
                    <button
                        type="button"
                        onClick={() => setQuery('')}
                        aria-label={t('search.clear')}
                    >
                        <LuX aria-hidden="true"/>
                    </button>
                )}
            </div>
            {(normalizedQuery) && (
                <section className={styles.searchResults} aria-live="polite">
                    <div className={styles.searchResultCount}>
                        {t('search.resultCount', {count: results.length})}
                    </div>
                    {(results.length === 0) ? (
                        <div className={styles.emptySearch}>
                            <LuSearch aria-hidden="true"/>
                            <h2>
                                {t('search.noResultsTitle')}
                            </h2>
                            <p>
                                {t('search.noResultsDescription')}
                            </p>
                        </div>
                    ) : (
                        <div className={styles.searchResultList}>
                            {results.map((article) => (
                                <Link
                                    key={article.slug}
                                    href={`/help-center/${article.slug}`}
                                    aria-label={t('openArticle', {article: article.title})}
                                >
                                    <span>
                                        <small>
                                            {article.categoryTitle}
                                        </small>
                                        <strong>
                                            {article.title}
                                        </strong>
                                        <span>
                                            {article.summary}
                                        </span>
                                    </span>
                                    <LuArrowRight aria-hidden="true"/>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
